import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { api, onSessionChange, refreshSession, setSession } from "./api";
import { resetSuspensions } from "./suspension";
import type { AuthResponse, User } from "./types";

type Status = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  status: Status;
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (input: {
    email: string;
    password: string;
    name: string;
    organization_name?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUserState] = useState<User | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const off = onSessionChange((session) => {
      setUserState(session?.user ?? null);
      setStatus(session ? "authenticated" : "anonymous");
    });

    // Silent sign-in from the HttpOnly refresh cookie.
    void refreshSession();

    return () => {
      off();
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const session = await api.post<AuthResponse>(
      "auth/login",
      { email, password },
      { auth: false, cookie: true },
    );
    setSession(session);
  }, []);

  const register = useCallback<AuthContextValue["register"]>(async (input) => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const session = await api.post<AuthResponse>(
      "auth/register",
      { ...input, timezone },
      { auth: false, cookie: true },
    );
    setSession(session);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("auth/logout", undefined, { cookie: true });
    } finally {
      setSession(null);
      queryClient.clear();
      resetSuspensions();
    }
  }, [queryClient]);

  const value = useMemo(
    () => ({ status, user, login, register, logout, setUser: setUserState }),
    [status, user, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");

  return ctx;
}
